import { Suspense, useContext, useEffect } from 'react';
import type { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Routes as ReactRoutes, Route } from 'react-router-dom';

import { UnleashKeys } from '../constants/unleash-keys';
import { AppConfigErrorScreen } from '../features/app-config/app-config-error-screen/app-config-error-screen';
import { useLocations } from '../features/app-config/hooks/use-locations';
import { useFeatureToggleConfig } from '../features/app-config/hooks/use-query/use-feature-toggle-config';
import { useSelectedRootLocationConfiguration } from '../features/app-config/hooks/use-selected-root-location-configuration';
import { useIsSchedulerEnabled } from '../features/checklist/checklist-scheduler/hooks/use-is-scheduler-enabled';
import { useFeatureObservationsOnIdm } from '../hooks/feature-flags/use-feature-observations-on-idm';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { useCurrentUserContext } from '../providers/current-user-provider/current-user-provider';
import { useAppConfigContext } from '../providers/is-idm-provider/app-config-provider';

import {
  ActivityChecklistMobile,
  ActivityDesktop,
  ActivityDetailsView,
  ActivityListView,
  ActivityPlanningView,
  AppConfigView,
  Asset3d,
  AssetActivities,
  AssetDetails,
  AssetDocuments,
  AssetNotifications,
  AssetProperties,
  AssetTrends,
  ChecklistItemObservationListMobile,
  DataModelingOverridePage,
  IdmObservationMobileOverviewPage,
  IdmObservationsDesktopFormPage,
  IdmObservationsDesktopTablePage,
  IdmObservationsMobileListPage,
  LandingPage,
  NotificationDetailsView,
  ObservationDetailsMobile,
  ObservationPageDesktop,
  ObservationsOverview,
  PageLayout,
  PageNotFound,
  SchedulesPage,
  TemplateCreatedConfirmationPage,
  TemplatesMobile,
  TemplatesOverviewNew,
  UserProfilePage,
} from './lazy-route-pages';
import { PageLoader } from './page-loader/page-loader';
import type { RoutesContextInterface } from './routes.context';
import { RoutesContext } from './routes.context';
import { SchedulesErrorBoundary } from './schedules/schedules-error-boundary';
import {
  getIsChecklistPageEnabled,
  getIsObservationsPageEnabled,
  getIsOverviewPageEnabled,
  getIsTemplatesPageEnabled,
  getLandingPagePath,
} from './utils';

const routesContextDefaultValue: RoutesContextInterface = {
  useAppConfigContext,
  useIsDesktop,
  useFeatureObservationsOnIdm,
  useSelectedRootLocationConfiguration,
  useCurrentUserContext,
  useLocations,
  useFeatureToggleConfig,
  useIsSchedulerEnabled,
  AppConfigErrorScreen,
  PageLayout,
  LandingPage,
  AppConfigView,
  UserProfilePage,
  ActivityDesktop,
  ActivityListView,
  ActivityPlanningView,
  ChecklistItemObservationListMobile,
  ActivityDetailsView,
  TemplatesMobile,
  TemplatesOverviewNew,
  TemplateCreatedConfirmationPage,
  SchedulesPage,
  ObservationsOverview,
  AssetDetails,
  Asset3d,
  AssetTrends,
  AssetProperties,
  AssetActivities,
  AssetNotifications,
  NotificationDetailsView,
  AssetDocuments,
  ActivityChecklistMobile,
  ObservationPageDesktop,
  ObservationDetailsMobile,
  IdmObservationsDesktopTablePage,
  IdmObservationsDesktopFormPage,
  IdmObservationsMobileListPage,
  IdmObservationMobileOverviewPage,
  PageNotFound,
  DataModelingOverridePage,
};

export const Routes: FunctionComponent = () => {
  const {
    useAppConfigContext,
    useIsDesktop,
    useFeatureObservationsOnIdm,
    useSelectedRootLocationConfiguration,
    useCurrentUserContext,
    useLocations,
    useFeatureToggleConfig,
    useIsSchedulerEnabled,
    AppConfigErrorScreen,
    PageLayout,
    LandingPage,
    AppConfigView,
    UserProfilePage,
    ActivityDesktop,
    ActivityListView,
    ActivityPlanningView,
    ChecklistItemObservationListMobile,
    ActivityDetailsView,
    TemplatesMobile,
    TemplatesOverviewNew,
    TemplateCreatedConfirmationPage,
    SchedulesPage,
    ObservationsOverview,
    AssetDetails,
    Asset3d,
    AssetTrends,
    AssetProperties,
    AssetActivities,
    AssetNotifications,
    NotificationDetailsView,
    AssetDocuments,
    ActivityChecklistMobile,
    ObservationPageDesktop,
    ObservationDetailsMobile,
    IdmObservationsDesktopTablePage,
    IdmObservationsDesktopFormPage,
    IdmObservationsMobileListPage,
    IdmObservationMobileOverviewPage,
    PageNotFound,
    DataModelingOverridePage,
  } = useContext(RoutesContext) ?? routesContextDefaultValue;

  const { isIdm } = useAppConfigContext();
  const isDesktop = useIsDesktop();

  const isObservationsFeatureEnabledOnIdm = useFeatureObservationsOnIdm();

  const { t } = useTranslation('user');

  const selectedRootLocation = useSelectedRootLocationConfiguration();
  const { user: userData } = useCurrentUserContext();
  const { handleSaveLocation } = useLocations();

  const {
    config: rootLocationFeatureToggles,
    isLoading: isFeatureConfigLoading,
  } = useFeatureToggleConfig(
    selectedRootLocation?.externalId ||
      selectedRootLocation?.assetExternalId ||
      ''
  );

  const {
    isEnabled: isSchedulesPageEnabled,
    isLoading: isSchedulesPageEnabledLoading,
  } = useIsSchedulerEnabled(UnleashKeys.schedule_tab);

  useEffect(() => {
    if (isFeatureConfigLoading) return;
    if (selectedRootLocation !== undefined) return;

    const userLocationPreference = userData?.preferences?.infield?.location;
    if (userLocationPreference === undefined) return;

    // We have no valid selected root location. It might be because the user preference location is not within the config.
    // Clear the preferences to find out.
    handleSaveLocation(undefined);
  }, [
    handleSaveLocation,
    isFeatureConfigLoading,
    selectedRootLocation,
    userData?.preferences?.infield?.location,
  ]);

  const isObservationsFeatureEnabledOnApm = getIsObservationsPageEnabled(
    rootLocationFeatureToggles
  );
  const isOverviewPageEnabled = getIsOverviewPageEnabled(
    rootLocationFeatureToggles
  );
  const isChecklistPageEnabled = getIsChecklistPageEnabled(
    rootLocationFeatureToggles
  );
  const isTemplatesPageEnabled = getIsTemplatesPageEnabled(
    rootLocationFeatureToggles
  );

  const landingPagePath = getLandingPagePath({
    isChecklistPageEnabled,
    isDesktop,
    isIdm,
    isObservationsFeatureEnabledOnIdm,
    selectedRootLocation,
  });

  if (isFeatureConfigLoading) {
    return (
      <PageLoader
        infoText={t(
          'loadingFeatureConfiguration',
          'Loading configured application features...'
        )}
      />
    );
  }

  if (selectedRootLocation === undefined) {
    return (
      <AppConfigErrorScreen
        title={t(
          'USER_ERROR_LOCATION_MISSING_TITLE',
          'Error: Location is missing.'
        )}
        body={t(
          'USER_ERROR_LOCATION_MISSING_DESCRIPTION',
          "The InField admin has not defined any location yet, or you dont't have access to any of the configured locations. Contact your admin if you have any questions."
        )}
      />
    );
  }

  return (
    <Suspense
      fallback={
        <PageLoader
          infoText={t('LOADING_APPLICATION', 'loading application...')}
        />
      }
    >
      <ReactRoutes>
        <Route path="/" element={<PageLayout page="mobile-explore" />}>
          <Route
            path="/"
            element={
              !selectedRootLocation ? (
                <PageLayout />
              ) : (
                <Navigate to={landingPagePath} />
              )
            }
          />
          <Route path="/explore" element={<LandingPage />} />
        </Route>

        <Route path="/" element={<PageLayout />}>
          <Route
            path="/application-configuration"
            element={<AppConfigView />}
          />
          <Route path="/profile" element={<UserProfilePage />} />
          <Route
            path="/profile/data-modeling-override"
            element={<DataModelingOverridePage />}
          />
          {isChecklistPageEnabled && (
            <Route
              path="/checklists"
              element={isDesktop ? <ActivityDesktop /> : <ActivityListView />}
            />
          )}
          {isOverviewPageEnabled && (
            <Route path="/overview" element={<ActivityPlanningView />} />
          )}
          {isObservationsFeatureEnabledOnApm && (
            <Route
              path="/checklistItem/:checklistItemId/observations"
              element={<ChecklistItemObservationListMobile />}
            />
          )}
          {rootLocationFeatureToggles?.workorders && (
            <Route
              path="/activity/:activityId/details"
              element={<ActivityDetailsView />}
            />
          )}
          {isTemplatesPageEnabled && (
            <>
              <Route
                path="/templates"
                element={
                  !isDesktop ? <TemplatesMobile /> : <TemplatesOverviewNew />
                }
              />
              <Route
                path="/templates/createdChecklist/:checklistExternalId"
                element={<TemplateCreatedConfirmationPage />}
              />
            </>
          )}
          {isSchedulesPageEnabled && (
            <Route
              path="/schedules"
              element={
                <SchedulesErrorBoundary>
                  <SchedulesPage />
                </SchedulesErrorBoundary>
              }
            />
          )}
          {!isSchedulesPageEnabled && !isSchedulesPageEnabledLoading && (
            <Route path="/schedules" element={<Navigate to="/checklists" />} />
          )}
          {isObservationsFeatureEnabledOnApm && (
            <Route path="/observations" element={<ObservationsOverview />} />
          )}
          {isObservationsFeatureEnabledOnIdm && (
            <>
              <Route
                path="/observations"
                element={
                  isDesktop ? (
                    <IdmObservationsDesktopTablePage />
                  ) : (
                    <IdmObservationsMobileListPage />
                  )
                }
              />
              <Route
                path="/observation"
                element={
                  isDesktop ? (
                    <IdmObservationsDesktopFormPage />
                  ) : (
                    <IdmObservationMobileOverviewPage />
                  )
                }
              />
              <Route
                path="/observation/:observationExternalId"
                element={
                  isDesktop ? (
                    <IdmObservationsDesktopFormPage />
                  ) : (
                    <IdmObservationMobileOverviewPage />
                  )
                }
              />
            </>
          )}
          <Route path="/asset/:assetId" element={<AssetDetails />} />
          {rootLocationFeatureToggles?.threeD && (
            <Route path="/asset/:assetId/3d" element={<Asset3d />} />
          )}
          {rootLocationFeatureToggles?.trends && (
            <Route path="/asset/:assetId/trends" element={<AssetTrends />} />
          )}
          <Route
            path="/asset/:assetId/properties"
            element={<AssetProperties />}
          />
          {rootLocationFeatureToggles?.workorders && (
            <Route
              path="/asset/:assetId/activities"
              element={<AssetActivities />}
            />
          )}
          {rootLocationFeatureToggles?.notifications && (
            <Route
              path="/asset/:assetId/notifications"
              element={<AssetNotifications />}
            />
          )}
          {rootLocationFeatureToggles?.notifications && (
            <Route
              path="/notification/:notificationId/details"
              element={<NotificationDetailsView />}
            />
          )}
          {rootLocationFeatureToggles?.documents && (
            <Route
              path="/asset/:assetId/documents"
              element={<AssetDocuments />}
            />
          )}
        </Route>

        {isChecklistPageEnabled && (
          <Route path="/" element={<PageLayout page="mobile-checklist" />}>
            <Route
              id="checklist"
              path="/activity/:activityId/checklist"
              element={<ActivityChecklistMobile />}
            />
          </Route>
        )}

        {isObservationsFeatureEnabledOnApm && (
          <Route path="/" element={<PageLayout page="mobile-observation" />}>
            <Route
              path="/observation"
              element={
                isDesktop ? (
                  <ObservationPageDesktop />
                ) : (
                  <ObservationDetailsMobile />
                )
              }
            />
            <Route
              path="/observation/:observationExternalId"
              element={
                isDesktop ? (
                  <ObservationPageDesktop />
                ) : (
                  <ObservationDetailsMobile />
                )
              }
            />
          </Route>
        )}

        {isSchedulesPageEnabledLoading && (
          <Route
            path="/schedules"
            element={
              <PageLoader
                infoText={t(
                  'loadingSchedulerAccess',
                  'Checking your access...'
                )}
              />
            }
          />
        )}

        {/* Catch-all route for 404 Not Found */}
        <Route path="*" element={<PageNotFound />} />
      </ReactRoutes>
    </Suspense>
  );
};
